const prefix = 'bybitapp://open';

interface OpenAppNativePathOptions {
  path: string;
  topPage?: boolean;
}

export async function openAppNativePath({
  path,
  topPage = false,
}: OpenAppNativePathOptions) {
  try {
    // Might throw in old versions of the app
    await window.BybitHecate.ready();
    await window.BybitHecate.Router.push({ path });
    if (topPage) window.BybitHecate.Router.pop2Home();
    return;
    // eslint-disable-next-line no-empty
  } catch {}
}
// 初始化
const initFn = () => {
  const script = document.createElement('script');
  script.src = `/common-static/cht-static/deeplink/jumpToApp.js?v=${new Date().getTime()}`;
  script.defer = true;
  document.body.appendChild(script);
};

function isApp() {
  return navigator.userAgent.toLowerCase().includes('bybit_app');
}

function getPlainPath(path: string) {
  let plainPath = path.replace(/\/[a-z]{2,3}-[A-Z]{2}\//, '/');

  if (!plainPath.endsWith('/')) {
    plainPath += '/';
  }

  return plainPath;
}

function isUserSecurity(path: string) {
  return path.startsWith('/app/user/security');
}

function isSpot(path: string) {
  return /^\/trade\/spot\/([A-Z\d]+\/[A-Z]+\/)?$/.test(path);
}

function isOption(path: string) {
  return path.startsWith('/trade/option/usdc/');
}

function isInversePerpetual(path: string) {
  return /^\/trade\/inverse\/([A-Z]+\/)?$/.test(path);
}

function isUSDTPerpetual(path: string) {
  return /^\/trade\/usdt\/([A-Z\d]+USDT\/)?$/.test(path);
}

function isInverseFutures(path: string) {
  return /^\/trade\/inverse\/futures\/[A-Z]+_(Q|BIQ|NBIQ)\/$/.test(path);
}

function isUSDCPerpetual(path: string) {
  return /^\/trade\/futures\/usdc\/([A-Z]+-[A-Z]+\/)?$/.test(path);
}

function isCreateSpotGridBot(path: string) {
  return path === '/tradingbot/create/';
}

function isCreateDCABot(path: string) {
  return path === '/tradingbot/dca-create/';
}

function isCreateFuturesGridBot(path: string) {
  return path === '/tradingbot/fgrid-create/';
}

function isCreateFMARTBot(path: string) {
  return path === '/tradingbot/fmart-create/';
}

function isSavings(path: string) {
  return ['/earn/flexible-staking/', '/earn/savings/'].includes(path);
}

function isByfiAccount(path: string, url: URL) {
  return (
    // ByFi account
    (path === '/user/assets/home/financial/' &&
      url.searchParams.get('faTransfer') !== 'true') ||
    [
      // ByFi records
      '/earn/wallet-records/',
      // ByFi orders
      '/user/assets/order/financial-defi-orders/',
      '/user/assets/order/financial-defi-orders/defi-order/',
      '/user/assets/order/financial-dual-asset-orders/',
      '/user/assets/order/financial-dual-asset-orders/dual-asset-order/',
      '/user/assets/order/financial-launchpool-orders/',
      '/user/assets/order/financial-launchpool-orders/launchpool-order/',
      '/user/assets/order/financial-flexible-staking-orders/',
      '/user/assets/order/financial-flexible-staking-orders/flexible-staking-order/',
    ].includes(path)
  );
}

function isTransfer(path: string, url: URL) {
  return (
    path === '/user/assets/home/financial/' &&
    url.searchParams.get('faTransfer') === 'true'
  );
}

function isRewardsDetail(path: string) {
  return path === '/task-center/reward_detail/';
}
function isTaskDetail(path: string) {
  return path === '/task-center/task_detail/';
}

/**
 * 需求文档：https://c1ey4wdv9g.larksuite.com/wiki/wikus2XyFsJfNFa8aQ23Xw7khOd
 *
 * spot trading 和 margin trading 的路径相同，均为：/trade/spot/BTC/USDT
 * 但是业务方需求要在APP中打开不同tab
 *
 * 因此需要可以额外配置deeplink参数，并且不能和web端的参数重复；
 * 因此在这里做一个约定：
 * 1. 在web端配置的需要deeplink透传的参数，以【deeplink_params__】为前缀；
 * 2. deeplink打开任意APP页面会携带web链接中所有的deeplink参数并去掉前缀；
 *
 * @example
 * 原本无参数：
 * /trade/spot/BTC/USDT
 * -> bybitapp://open/home?tab=3&symbol=BTCUSDT
 *
 * 带参数：
 * /trade/spot/BTC/USDT?deeplink_params__tabName=margin&deeplink_params__page=trading
 * -> bybitapp://open/home?tab=3&symbol=BTCUSDT&tabName=margin&page=trading
 *
 */
function resolveDeeplinkParams(url: URL) {
  let params = '';
  const deeplinkParamsPrefix = 'deeplink_params__';
  url.searchParams.forEach((value, key) => {
    if (key.startsWith(deeplinkParamsPrefix)) {
      params = `${params}&${key.replace(deeplinkParamsPrefix, '')}=${value}`;
    }
  });
  return params;
}

type AppType = 'by' | 'by-mini';

interface AbsoluteNativeDeepLinkMap {
  // httpPath: [deeplink, topPage]
  [httpPath: string]: [deeplink: string, topPage?: boolean];
}
// 绝对路径定位 打开原生的场合link
const absoluteNativeDeepLinkMap: AbsoluteNativeDeepLinkMap = {
  '/login/': ['/login'],
  '/register/': ['/signup?directlyRegister=true'],
  '/user/accounts/auth/personal/': ['/user/verify/kyc'],
  '/fiat/deposit/': ['/recharge'],
  '/fiat/trade/deposit/home/': [
    '/route?targetUrl=fiat_deposit-fiat-deposit-home',
  ],
  '/fiat/trade/withdraw/home/': ['/withdraw'],
  '/app/aboutus/aboutus/': ['/aboutus'],
  '/user/assets/home/tradingbot/': ['/assets_account?account=12', true],
  '/user/assets/home/tradingaccount/': ['/assets_account?account=5', true],
  '/earn/dual-asset-mining/': ['/byficenter?type=2'],
  '/earn/launchpool/': ['/byficenter?type=3'],
  '/earn/liquidity-mining/': ['/byficenter?type=5'],
  '/apex/trade/': ['/apex'],
  '/dashboard/': ['/home?tab=0'],
  '/tax-api/': ['/home?tab=0'],
  // '/app/user/security/': ['/user_avatar_page', true],
  '/trade/spot/lending/': ['/spot/lending/lend'],
};

interface AbsoluteNativeAppletsDeepLinkMap {
  // httpPath: [deeplink, topPage,appType,]
  [httpPath: string]: [deeplink: string, appType?: AppType];
}
const absoluteNativeAppletsDeepLinkMap: AbsoluteNativeAppletsDeepLinkMap = {
  // 以下是by-mini开头的小程序
  '/earn/pos/': ['earn/eth2Home'],
  '/earn/double-win/': ['earn/doubleWinHome'],
  '/earn/discount-buy/': ['earn/discountBuy'],
  '/tradingbot/': ['trading_bot/home'],
  '/tradingbot/my-bot/': ['trading_bot/myBots?tab=0'],
  '/referral/': ['referral/referral'],
  '/launch/': ['by_launch/index'],
  '/task-center/rewards_hub/': ['rewardshub/home'],
  '/task-center/my_campaigns/': ['rewardshub/mycampaigns'],
  '/task-center/my_rewards/': ['rewardshub/myrewards'],
  '/fiat/cards/apply/': ['by_card/card-empty'],
  '/fiat/cards/': ['by_card/index'],
  '/tools-discovery/': ['tools_discovery/index'],
  '/copyTrade/': ['copy_trade/index'],
  '/copyTrade/trade-center/leader/': ['copy_trade/trader_center'],
  '/copyTrade/trade-center/detail/': ['copy_trade/profile'],
  '/nft/': ['NFT/index?initialTab=home'],
  '/giveaway/': ['red_envelope/claim_preview'],
  '/web3/rewards/': ['web3_applet/metropolis'],
  '/web3/ido/detail/': ['web3_applet/ido'],
  '/vip-program/': ['vip/home'],
  '/VIPProgram/': ['vip/home'],
  '/VIPProgram/intro/': ['vip/home'],
  '/fiat/cards': ['by_card/landing'],

  // 以下是by开头的小程序
  '/web3/': ['web3/tab/index?index=0', 'by'],
  '/web3/swap/': ['web3/tab/index?tag=swap', 'by'],
  '/web3/staking/': ['web3/staking/introduction', 'by'],
  '/web3/ido/': ['web3/ido/list', 'by'],
  '/web3/crypto/': ['web3/fiat/buycrypto', 'by'],
  '/web3/balance/': ['web3/tab/index?tag=asset', 'by'],
};
interface IncludesDeepLinkItem {
  list?: Array<string>;
  regex?: RegExp;
  deeplink: string;
  topPage: boolean;
  appType?: AppType;
}

const includesDeepLinkList: IncludesDeepLinkItem[] = [
  {
    // 交易赛添加的特殊需求 https://jira.yijin.io/browse/GA-2171
    list: ['/trade/spot/act/lt-landing-page/'],
    deeplink: '/home?tab=1&pageType=spot&token=usdt&tag=16',
    topPage: true,
  },
  {
    list: [
      '/data/markets/',
      '/data/markets/collect/',
      '/data/markets/contract/',
      '/data/markets/spot/',
      '/data/markets/reference/',
    ],
    deeplink: '/home?tab=1',
    topPage: true,
  },
  {
    list: [
      // Spot account
      '/user/assets/home/spot/',
      // Spot records
      '/user/assets/records/trade-spot/',
      '/user/assets/records/trade-spot/deposit/',
      '/user/assets/records/trade-spot/withdraw/',
      '/user/assets/records/trade-spot/transfer/',
      '/user/assets/records/trade-spot/fiat/',
      '/user/assets/records/trade-spot/p2p/',
      '/user/assets/records/trade-spot/other/',
      // Spot orders
      '/user/assets/order/spot-current-orders/active/',
      '/user/assets/order/spot-current-orders/conditional/',
      '/user/assets/order/spot-orders/active/',
      '/user/assets/order/spot-orders/conditional/',
      '/user/assets/order/spot-history/active/',
    ],
    deeplink: '/assets_account?account=1',
    topPage: true,
  },
  {
    list: [
      // Derivatives account
      '/user/assets/home/trading/',
      // Derivatives records
      '/user/assets/records/trade/',
      '/user/assets/records/trade/deposit/',
      '/user/assets/records/trade/exchange/',
      '/user/assets/records/trade/other/',
      // Derivatives orders
      '/user/assets/order/all-orders/',
      '/user/assets/order/all-orders/inverse/',
      '/user/assets/order/all-orders/usdt/',
      '/user/assets/order/all-orders/futures/',
      '/user/assets/order/closed-pnl/',
      '/user/assets/order/closed-pnl/inverse/',
      '/user/assets/order/closed-pnl/usdt/',
      '/user/assets/order/closed-pnl/futures/',
      '/user/assets/order/trade-history/',
      '/user/assets/order/trade-history/inverse/',
      '/user/assets/order/trade-history/usdt/',
      '/user/assets/order/trade-history/futures/',
    ],
    deeplink: '/assets_account?account=2',
    topPage: true,
  },
  {
    list: [
      '/user/assets/home/usdcaccount/',
      '/trade/usdc/assets/transactionlogs/',
    ],
    deeplink: '/assets_account?account=4',
    topPage: true,
  },
  {
    list: [
      '/user/assets/home/fiat/',
      '/user/assets/records/fiat/transfer/',
      '/user/assets/records/fiat/fiat/',
      '/user/assets/records/fiat/p2p/',
      '/user/assets/records/fiat/depositFiat/',
    ],
    deeplink: '/assets_account?account=6',
    topPage: true,
  },
  {
    list: ['/user/assets/home/', '/user/assets/home/overview/'],
    deeplink: '/assets_account',
    topPage: true,
  },
  {
    list: ['/fiat/purchase/crypto/'],
    deeplink: '/fiat/buycrypto',
    topPage: false,
  },
  {
    list: ['/fiat/trade/express/home/'],
    deeplink: '/fiat/expressHome',
    topPage: false,
  },

  {
    list: [
      '/fiat/trade/otc/',
      '/fiat/trade/otc/advertiser/',
      '/fiat/trade/otc/advertiser/all/',
      '/fiat/trade/otc/orderList/',
      '/fiat/trade/otc/profile/',
    ],
    deeplink: '/otc_list',
    topPage: false,
  },
  {
    regex: /^\/trade\/option\/usdc\/([A-Z]+\/)?$/,
    deeplink: '/home?tab=2&type=option',
    topPage: true,
  },
  {
    list: ['/earn/', '/earn/home/'],
    deeplink: '/byficenter',
    topPage: false,
  },
  // {
  //   list: ['/earn/flexible-staking/', '/earn/savings/'],
  //   deeplink: '/byficenter?type=4',
  //   topPage: false
  // },
  {
    list: ['/earn/wealth-management/'],
    deeplink: 'earn/fundPoolHome',
    topPage: false,
    appType: 'by-mini',
  },
];

/**
 * 和jack.luo确认过，aa?bb=1&cc=2&&bb=1，这种场合不影响，所以没有过滤
 * mergeURL('web3/tab/index', '?a=2&bc=1') => web3/tab/index?a=2&bc=1
 * mergeURL('web3/tab/index?tab=1', '?a=2&bc=1') => web3/tab/index?tab=1&a=2&bc=1
 * mergeURL('web3/tab/index?tab=1', '?a=2&bc=1?ee=11') => web3/tab/index?tab=1&a=2&bc=1&ee=11
 * mergeURL('web3/tab/index?tab=1') => web3/tab/index?tab=1
 * mergeURL('web3/tab/index') => web3/tab/index
 * @param {*} a
 * @param {*} b
 * @returns https://www.bybit.com/en-US/web3/ido/detail
 */
function mergeURL(a, b = '') {
  if (!b.trim()) {
    return a;
  }
  if (a.indexOf('?') !== -1 && b.indexOf('?') !== -1) {
    b = b.replace(/\?/g, '&');
  }
  return a + b;
}

export function getNativePath(
  to: string
): { path: string; topPage: boolean } | void {
  const url = new URL(to, window.location.href);
  const path = getPlainPath(url.pathname);
  const search = url.search;
  const searchParams = url.searchParams;

  // app内有两种小程序开头by: 一般是web3
  const getAppMiniPath = (p: string, type: AppType = 'by-mini') => {
    const path = mergeURL(p, search);
    return `${prefix}/route?targetUrl=${encodeURIComponent(
      `${type}://${path}`
    )}`;
  };
  // 新版deeplink
  if (typeof window !== 'undefined' && (window as any)._mapToApp) {
    const path = (window as any)._mapToApp(url);
    if (path.deepLinkUrl || path.backUpDeepLinkUrl) {
      return {
        path: path.deepLinkUrl || path.backUpDeepLinkUrl,
        topPage: false,
      };
    }
  }
  // 1. 过滤绝对路径-原生程序
  const nativeLinkMap = absoluteNativeDeepLinkMap[path];
  if (nativeLinkMap) {
    const [deeplink, topPage = false] = nativeLinkMap;
    const path = prefix + deeplink;
    return { path, topPage };
  }
  // 2. 过滤绝对路径-小程序
  const nativeAppletsLinkMap = absoluteNativeAppletsDeepLinkMap[path];
  if (nativeAppletsLinkMap) {
    const [deeplink, appType = 'by-mini'] = nativeAppletsLinkMap;
    const path = getAppMiniPath(deeplink, appType);
    return { path, topPage: false };
  }

  // 3. 过滤包含路径 & 正则路径
  for (const includesDeepLinkItem of includesDeepLinkList) {
    const { list, regex, deeplink, topPage, appType } = includesDeepLinkItem;
    let pathWithPrefix = prefix + deeplink;
    if (appType) {
      pathWithPrefix = getAppMiniPath(deeplink, appType);
    }
    if (list && list.includes(path)) {
      return { path: pathWithPrefix, topPage };
    }
    if (regex && regex.test(path)) {
      return { path: pathWithPrefix, topPage };
    }
  }

  // 4. 以下都是需要特殊处理的路径和相应参数
  if (isUserSecurity(path)) {
    if (searchParams.get('page') === 'user_avatar_page') {
      return {
        path: `${prefix}/user_avatar_page`,
        topPage: true,
      };
    }
  }

  if (isSpot(path)) {
    const pair = path.replace('/trade/spot/', '');
    return {
      path: `${prefix}/home?tab=3${
        pair ? `&symbol=${pair.split('/').join('')}` : ''
      }${resolveDeeplinkParams(url)}`,
      topPage: true,
    };
  }

  if (isOption(path)) {
    return {
      path: `${prefix}/home?tab=2&page=option${resolveDeeplinkParams(url)}`,
      topPage: true,
    };
  }

  if (isInversePerpetual(path)) {
    const pair = path.replace('/trade/inverse/', '').replace('/', '');
    return {
      path: `${prefix}/home?tab=2&type=inverse${pair ? `&symbol=${pair}` : ''}`,
      topPage: true,
    };
  }

  if (isUSDTPerpetual(path)) {
    const pair = path.replace('/trade/usdt/', '').replace('/', '');
    return {
      path: `${prefix}/home?tab=2&type=usdt${pair ? `&symbol=${pair}` : ''}`,
      topPage: true,
    };
  }

  if (isSavings(path)) {
    const id = url.searchParams.get('id');
    const type = url.searchParams.get('type');
    return {
      path: `${prefix}/byficenter?${type ? `type=${type}` : 'type=4'}${
        id ? '&id=' + id : ''
      }`,
      topPage: false,
    };
  }

  if (isInverseFutures(path)) {
    const pair = path
      .replace('/trade/inverse/futures/', '')
      // Very fragile!!!
      .replace('_Q/', '0930')
      .replace('_BIQ/', '1230')
      // Useless for now
      .replace('_NBIQ/', '1230');
    return {
      path: `${prefix}/home?tab=2&type=inverse${pair ? `&symbol=${pair}` : ''}`,
      topPage: true,
    };
  }

  if (isUSDCPerpetual(path)) {
    const pair = path.replace('/trade/futures/usdc/', '').replace('/', '');
    return {
      path: `${prefix}/home?tab=2&type=usdc${pair ? `&symbol=${pair}` : ''}`,
      topPage: true,
    };
  }

  if (isCreateSpotGridBot(path)) {
    const base = url.searchParams.get('base');
    const quote = url.searchParams.get('quote');
    return {
      path: getAppMiniPath(
        `trading_bot/createGridBot${
          base && quote ? `?symbol=${base}${quote}` : ''
        }`
      ),
      topPage: false,
    };
  }

  if (isCreateDCABot(path)) {
    const base = url.searchParams.get('base');
    const quote = url.searchParams.get('quote');
    return {
      path: getAppMiniPath(
        `trading_bot/createDcaBot${
          base && quote ? `?coin=${base}&currency=${quote}` : ''
        }`
      ),
      topPage: false,
    };
  }

  if (isCreateFuturesGridBot(path)) {
    const base = url.searchParams.get('base');
    const quote = url.searchParams.get('quote');
    return {
      path: getAppMiniPath(
        `trading_bot/createFutures${
          base && quote ? `?symbol=${base}${quote}` : ''
        }`
      ),
      topPage: false,
    };
  }

  if (isCreateFMARTBot(path)) {
    const base = url.searchParams.get('base');
    const quote = url.searchParams.get('quote');
    return {
      path: getAppMiniPath(
        `trading_bot/createFMart${
          base && quote ? `?symbol=${base}${quote}` : ''
        }`
      ),
      topPage: false,
    };
  }

  if (isByfiAccount(path, url)) {
    return {
      path: `${prefix}/assets_account?account=3`,
      topPage: true,
    };
  }

  if (isTransfer(path, url)) {
    const coin = url.searchParams.get('faCoin');
    return {
      path: `${prefix}/transfer${coin ? `?coin=${coin}` : ''}`,
      topPage: false,
    };
  }

  if (isRewardsDetail(path)) {
    const id = url.searchParams.get('reward-id');
    const spec_code = url.searchParams.get('reward-code');
    return {
      path: getAppMiniPath(
        `rewardshub/rewarddetail?awardId=${id}&specCode=${spec_code}`
      ),
      topPage: false,
    };
  }

  if (isTaskDetail(path)) {
    const taskId = url.searchParams.get('task-id');
    const ruleIndex = url.searchParams.get('rule-index') || 0;
    return {
      path: getAppMiniPath(
        `rewardshub/taskdetailspage?taskId=${taskId}&ruleIndex=${ruleIndex}`
      ),
      topPage: false,
    };
  }
}

function appInNavigateByUrl(url: string) {
  // mailto: and tel: 通过app打开原生链接
  if (url.startsWith('mailto:') || url.startsWith('tel:')) {
    try {
      window.BybitHecate.UrlLauncher.open({ url });
      // eslint-disable-next-line no-empty
    } catch {}
    return true;
  }
  // deeplink，直接打开
  if (url.startsWith(prefix)) {
    console.log('???', url);
    openAppNativePath({ path: url });
    return true;
  }
  // bymini || by  link，转译后打开
  if (url.startsWith('by-mini:') || url.startsWith('by:')) {
    openAppNativePath({
      path: `${prefix}/route?targetUrl=${encodeURIComponent(url)}`,
    });
    return true;
  }
  const nativePath = getNativePath(url);
  if (nativePath) {
    openAppNativePath(nativePath);
    return true;
  }
}

export async function navigate(
  url: string,
  options?: { newTab?: boolean; tryOpenApp?: boolean }
) {
  if (isApp()) {
    const isNavigated = appInNavigateByUrl(url);
    if (isNavigated) {
      return;
    } else {
      if (options?.newTab) {
        window.BybitHecate?.Router?.push({
          path: url,
        });
      } else {
        window.location.href = url;
      }
      return;
    }
  } else if (options?.tryOpenApp) {
    const nativePath = getNativePath(url);
    console.log('nativePath', nativePath);
    if (nativePath) {
      if (options.newTab) {
        window.open(nativePath.path);
      } else {
        window.location.href = nativePath.path;
      }
      return;
    }
  }

  if (options?.newTab) {
    window.open(url);
  } else {
    window.location.href = url;
  }
}

export function injectDeepLink() {
  if (!isApp()) {
    return;
  }
  initFn();
  document.addEventListener('click', async (event: MouseEvent) => {
    const target = (event.target as HTMLElement).closest('a');
    if (!target) return;
    const href = target.getAttribute('href');
    if (!href || href.startsWith('#')) return;
    if (href.startsWith('mailto:') || href.startsWith('tel:')) {
      event.preventDefault();
      try {
        window.BybitHecate.UrlLauncher.open({ url: href });
        // eslint-disable-next-line no-empty
      } catch {}
      return;
    }
    if (href.startsWith(prefix)) {
      event.preventDefault();
      openAppNativePath({ path: href });
      return;
    }
    if (href.startsWith('by-mini:') || href.startsWith('by:')) {
      event.preventDefault();
      openAppNativePath({
        path: `${prefix}/route?targetUrl=${encodeURIComponent(href)}`,
      });
      return;
    }
    const nativePath = getNativePath(href);
    if (!nativePath) return;
    event.preventDefault();
    openAppNativePath(nativePath);
  });
}
